import { useMemo } from 'react';

import { SingSetup, Song } from '~/interfaces';
import { accuracyOverTime } from '~/modules/game-engine/game-state/helpers/accuracy-over-time';
import { calculateNoteStats } from '~/modules/game-engine/game-state/helpers/calculate-note-stats';
import PlayersManager from '~/modules/players/players-manager';
import { ownsSection } from '~/routes/game/singing/game-overlay/helpers/pass-the-mic-turn';
import useHighScores from '~/routes/game/singing/post-game/hooks/use-high-scores';
import PostGameView from '~/routes/game/singing/post-game/post-game-view';
import LayoutGame from '~/routes/layout-game';
import { UnassignOnSongFinishedSetting } from '~/routes/settings/settings-state';

import GameState from '../../../../modules/game-engine/game-state/game-state';

interface Props {
  width: number;
  height: number;
  song: Song;
  onClickSongSelection: () => void;
  singSetup: SingSetup;
}

function PostGame({ song, width, height, onClickSongSelection, singSetup }: Props) {
  const highScores = useHighScores(song, singSetup);
  const playerScores = useMemo(
    () =>
      PlayersManager.getPlayers().map((player) => {
        // Read here rather than from the stored play: `GameState` still holds the sung notes at
        // this point, and this screen is shown for practice runs' sake too, which store nothing.
        const playerState = GameState.getPlayer(player.number);

        return {
          name: player.getName(),
          playerNumber: player.number,
          detailedScore: GameState.getPlayerDetailedScore(player.number),
          notes: playerState
            ? calculateNoteStats(playerState.getPlayerNotes(), playerState.getTrack(), GameState.getTolerance(), {
                // Marked on the sections that were theirs to sing, so pass the mic does not count a
                // partner's lines against them.
                ownsSection: (section) => ownsSection(player.number, section),
              })
            : undefined,
          accuracyOverTime: playerState
            ? accuracyOverTime(playerState.getPlayerNotes(), playerState.getTrack(), GameState.getTolerance(), {
                ownsSection: (section) => ownsSection(player.number, section),
              })
            : undefined,
        };
      }),
    [],
  );

  return (
    <LayoutGame>
      <PostGameView
        singSetup={singSetup}
        song={song}
        width={width}
        height={height}
        onClickSongSelection={() => {
          onClickSongSelection();

          if (UnassignOnSongFinishedSetting.get()) {
            PlayersManager.getPlayers().forEach((player) => {
              if (player.input.source === 'Remote Microphone') {
                PlayersManager.removePlayer(player.number);
              }
            });
          }
        }}
        players={playerScores}
        highScores={highScores}
      />
    </LayoutGame>
  );
}

export default PostGame;
